/* ==========================================================================
   Tükenenler sonda — müşteri seçimli sıralamada iki aşamalı listeleme
   --------------------------------------------------------------------------
   Varsayılan koleksiyon sırası "Farmahanem Stok Sıralama" uygulaması sayesinde
   zaten doğru. Müşteri başka bir sıralama seçince Shopify manuel sırayı
   kullanmaz; bu script ızgarayı şu sanal listenin ilgili sayfasıyla değiştirir:
     [stoktakiler (seçilen sırayla)] ++ [tükenenler (seçilen sırayla)]

   Ne zaman çalışacağına Liquid karar verir: ızgarada
   [data-farmahanem-stock-order] varsa (main-collection / main-search).
   Kümeler Section Rendering API + filter.v.availability=1|0 ile çekilir;
   kart HTML'i temanın kendisidir (hızlı ekle, rozetler aynen çalışır).
   İstek başarısız olursa ya da 3 sn'de bitmezse Shopify'ın kendi sırası kalır.
   Spec: TEMA-EK-SPEC.md
   ========================================================================== */

const GRID_SELECTOR = '.product-grid[data-farmahanem-stock-order]';
const PENDING_CLASS = 'fh-stock-order-pending';
const TIMEOUT_MS = 3000;
const CACHE_MS = 5 * 60 * 1000;

const processed = new WeakSet();
let activeController = null;

/** Mevcut URL parametreleri (sayfa hariç): sort_by, q, filtreler... */
function baseParams() {
  const params = new URLSearchParams(window.location.search);
  params.delete('page');
  params.delete('section_id');
  return params;
}

/* Stoktaki ürün sayısı (A) aynı yol + parametreler için kısa süre saklanır;
   sonraki sayfalarda gereksiz istek atılmaz. Storage kapalıysa sessizce geçer. */
function readCachedCount(key) {
  try {
    const data = JSON.parse(sessionStorage.getItem(key));
    return data && Date.now() - data.t < CACHE_MS ? data.count : null;
  } catch (error) {
    return null;
  }
}

function writeCachedCount(key, count) {
  try {
    sessionStorage.setItem(key, JSON.stringify({ count, t: Date.now() }));
  } catch (error) {
    /* storage kapalı — önbelleksiz devam */
  }
}

/** Bir kümenin (1 = stokta, 0 = tükenen) k. sayfası: toplam + kart düğümleri. */
async function fetchSet(sectionId, params, availability, page, signal) {
  const query = new URLSearchParams(params);
  query.set('filter.v.availability', availability);
  query.set('page', String(page));
  query.set('section_id', sectionId);

  const response = await fetch(`${window.location.pathname}?${query}`, { signal });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);

  const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
  const totalEl = doc.querySelector('[data-farmahanem-stock-total]');
  const grid = doc.querySelector('.product-grid');
  return {
    total: totalEl ? Number(totalEl.dataset.farmahanemStockTotal) || 0 : 0,
    items: grid ? Array.from(grid.children) : []
  };
}

function createDivider(label) {
  const divider = document.createElement('div');
  divider.className = 'fh-stock-divider';
  divider.textContent = label;
  return divider;
}

async function reorder(grid) {
  processed.add(grid);

  if (activeController) activeController.abort();
  const controller = new AbortController();
  activeController = controller;
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);

  const { signal } = controller;
  const sectionId = grid.dataset.farmahanemStockSection;
  const pageSize = Number(grid.dataset.farmahanemStockPageSize) || 24;
  const page = Number(grid.dataset.farmahanemStockPage) || 1;
  const start = pageSize * (page - 1);
  const params = baseParams();
  const cacheKey = `fh-stock-a:${window.location.pathname}?${params}`;

  try {
    // A = stoktaki ürün sayısı. Bu sayfa A'ya düşüyorsa A'nın aynı numaralı
    // sayfası hem sayıyı hem ürünleri verir (tek istek).
    let inStockCount = readCachedCount(cacheKey);
    let inStockPage = null;
    if (inStockCount === null || start < inStockCount) {
      inStockPage = await fetchSet(sectionId, params, '1', page, signal);
      inStockCount = inStockPage.total;
      writeCachedCount(cacheKey, inStockCount);
    }

    // Hiç stoktaki ürün yoksa sıra zaten doğru.
    if (inStockCount === 0) return;

    const items = start < inStockCount ? inStockPage.items : [];
    let dividerIndex = -1;

    const missing = pageSize - items.length;
    if (missing > 0) {
      // Tükenenler kümesinde bu sayfaya düşen dilim: offset = start - A
      const offset = Math.max(0, start - inStockCount);
      const soldOutPage = Math.floor(offset / pageSize) + 1;
      const skip = offset % pageSize;

      const first = await fetchSet(sectionId, params, '0', soldOutPage, signal);
      let soldOut = first.items.slice(skip);
      if (soldOut.length < missing && skip > 0 && first.total > soldOutPage * pageSize) {
        const second = await fetchSet(sectionId, params, '0', soldOutPage + 1, signal);
        soldOut = soldOut.concat(second.items);
      }
      soldOut = soldOut.slice(0, missing);

      if (items.length > 0 && soldOut.length > 0) dividerIndex = items.length;
      items.push(...soldOut);
    }

    // Sanal listenin dışına düşen sayfa: Shopify'ın çıktısı kalsın.
    if (items.length === 0 || !grid.isConnected || signal.aborted) return;

    const fragment = document.createDocumentFragment();
    items.forEach((item, index) => {
      if (index === dividerIndex) {
        fragment.append(createDivider(grid.dataset.farmahanemStockDivider || ''));
      }
      // Giriş animasyonu beklemesin; kartlar hemen görünsün.
      item.removeAttribute('data-cc-animate');
      item.querySelectorAll('[data-cc-animate]').forEach((el) => el.removeAttribute('data-cc-animate'));
      fragment.append(document.adoptNode(item));
    });
    grid.replaceChildren(fragment);
  } catch (error) {
    if (error.name !== 'AbortError') console.error('[farmahanem-stock-order]', error);
  } finally {
    clearTimeout(timer);
    grid.classList.remove(PENDING_CLASS);
    if (activeController === controller) activeController = null;
  }
}

function scan() {
  document.querySelectorAll(GRID_SELECTOR).forEach((grid) => {
    if (!processed.has(grid)) reorder(grid);
  });
}

/* Tema AJAX ile sıralama/filtre/sayfa değiştirince [data-ajax-container]
   içeriğini (ya da section'ın tamamını) yeniler ve olay yaymaz. Section
   sarmalayıcısı kalıcı olduğu için onu izliyoruz; yeni ızgara gelince işlenir.
   Kendi değişikliğimiz yeni ızgara üretmediği için döngü oluşmaz. */
document.querySelectorAll('filter-container').forEach((container) => {
  const root = container.closest('.shopify-section') || container.parentElement;
  new MutationObserver(scan).observe(root, { childList: true, subtree: true });
});

scan();
